import React, { memo, useEffect, useRef, useState } from 'react';

import { Box } from '@mui/material';
import PropTypes from 'prop-types';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';

import LoadingIndicator from 'components/LoadingIndicator';
import {
  getAllOrganizations,
  getModulesForms,
  getModulesObservationsAndMeeting,
} from 'containers/GlobalWrapper/actions';
import {
  selectAllOrganizationsLoadingFlag,
  selectAllOrganizationsMap,
  selectLoggedUserData,
} from 'containers/GlobalWrapper/selectors';
import useAuthSession from 'hooks/useAuthSession';
import useCurrentUser from 'hooks/useCurrentUser';
import { useAddToProjectWithInvitationMutation } from 'redux/api/registration.api';
import { Routing } from 'routing/routing';
import localStorageUser from 'utils/localStorageUser';
import sessionStorageUser from 'utils/sessionStorageUser';

const DataValidation = ({ loggedUserData, children, onValidationSuccess = () => null }) => {
  // #region [auth]
  const { isAuthenticated, isLoading, loginWithIDP, logout } = useAuthSession();
  const { userId: currentUserId, email: currentUserEmail } = useCurrentUser();
  // #endregion

  // #region [router]
  const { idData } = useParams();
  const history = useHistory();
  // #endregion

  // #region [redux]
  const dispatch = useDispatch();
  const [addToProject] = useAddToProjectWithInvitationMutation();

  // toJS() here instead of in the selector avoids an unnecessary re-render
  const organizations = useSelector(selectAllOrganizationsMap()).toJS();
  const loadingAllOrganizations = useSelector(selectAllOrganizationsLoadingFlag());
  // #endregion

  // #region [sessionStorage]
  const deepLink = sessionStorageUser.getDeepLink();
  // #endregion

  // #region [states]
  const [addingUserToProject, setAddingUserToProject] = useState(deepLink !== null);
  const [processingForward, setProcessingForward] = useState(
    () => !!sessionStorageUser.getForwardRedirect(),
  );
  // #endregion

  // #region [refs]
  const recoveryAttemptedRef = useRef(false);
  // #endregion

  // #region [methods]
  const verifyEmailValidation = () => {
    const emailValidationData = localStorageUser.getEmailValidationLocation();
    history.replace(emailValidationData !== null ? emailValidationData : Routing.general.welcome);
  };

  const handleCallBackOrganizations = data => {
    const firstOrganization = data?.organizations[0];
    if (deepLink?.organizationId && deepLink?.projectId) {
      return history.replace(Routing.home.home(deepLink.organizationId, deepLink.projectId));
    }

    const invitationsLink = sessionStorageUser.getInvitationsLink();
    if (invitationsLink?.length > 0) {
      const firstInvitation = invitationsLink[0];
      if (firstInvitation?.organization_id && firstInvitation?.project_id) {
        sessionStorageUser.setInvitationsLink(invitationsLink.slice(1));
        return history.replace(
          Routing.home.home(firstInvitation.organization_id, firstInvitation.project_id),
        );
      }
    }

    const emailVerificationLinkData = localStorageUser.getEmailVerificationLink();
    if (emailVerificationLinkData) {
      return verifyEmailValidation();
    }

    if (!firstOrganization?.id) {
      return history.replace(Routing.general.welcome);
    }

    return onValidationSuccess();
  };
  // #endregion

  // #region [effects]
  useEffect(
    () => {
      if (idData && !addingUserToProject) {
        dispatch(getModulesObservationsAndMeeting(idData, () => dispatch(getModulesForms(idData))));
      }
    },
    [dispatch, idData, addingUserToProject],
  );

  useEffect(
    () => {
      (async () => {
        if (isLoading) return;
        if (!isAuthenticated) {
          history.replace('/login');
          return;
        }

        if (currentUserId && deepLink) {
          if (deepLink.intendedEmail && currentUserEmail !== deepLink.intendedEmail) {
            sessionStorageUser.setDeepLink(null);
            if (!organizations?.length) {
              dispatch(getAllOrganizations(handleCallBackOrganizations));
            } else {
              handleCallBackOrganizations({ organizations });
            }
          } else {
            setAddingUserToProject(true);
            await addToProject({ deepLink: deepLink.url });
            sessionStorageUser.setDeepLink(null);
            dispatch(getAllOrganizations(handleCallBackOrganizations));
            // addTimeout before displaying children to give server time to process
            // the new rights for the user
            setTimeout(() => setAddingUserToProject(false), 2000);
          }
        } else if (currentUserId && !organizations?.length) {
          dispatch(getAllOrganizations(handleCallBackOrganizations));
        } else if (currentUserId && organizations?.length) {
          handleCallBackOrganizations({ organizations });
        } else if (isAuthenticated && !currentUserId) {
          if (recoveryAttemptedRef.current) return;
          recoveryAttemptedRef.current = true;
          await loginWithIDP();
          if (!localStorageUser.getCurrentUserId()) {
            sessionStorage.setItem(
              'loginCallbackError',
              JSON.stringify({ code: 'authentication_unknown_user' }),
            );
            await logout();
          }
        }
      })();
    },
    [isLoading, isAuthenticated, currentUserId, currentUserEmail, idData],
  );

  useEffect(
    () => {
      if (!processingForward) return;

      const forward = sessionStorageUser.getForwardRedirect();
      if (!forward) {
        setProcessingForward(false);
        return;
      }

      if (!loggedUserData) return;

      const { projectId, organizationId, email } = forward;
      sessionStorageUser.setForwardRedirect(null);

      if (loggedUserData.email === email && organizationId) {
        history.replace(
          projectId
            ? Routing.home.home(organizationId, projectId)
            : Routing.home.projects(organizationId),
        );
      } else {
        setProcessingForward(false);
      }
    },
    [processingForward, loggedUserData, history],
  );
  // #endregion

  // #region [render]
  if (
    isLoading ||
    !isAuthenticated ||
    (isAuthenticated && !currentUserId) ||
    loadingAllOrganizations ||
    processingForward
  ) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <LoadingIndicator withBackground={false} />
      </Box>
    );
  }
  return !addingUserToProject && children;
  // #endregion
};

DataValidation.propTypes = {
  children: PropTypes.node,
  onValidationSuccess: PropTypes.func,
  loggedUserData: PropTypes.object,
};

const mapStateToProps = createStructuredSelector({
  loggedUserData: selectLoggedUserData(),
});

const withConnect = connect(
  mapStateToProps,
  null,
);

export default memo(compose(withConnect)(DataValidation));
